import * as React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Check } from 'lucide-react'
import type { BookingLineDto, BookingPacketDto } from '@lib/dto/sales-booking.dto'
import { dollarsFromCents } from '@lib/helpers/format'
import { milliToPercent, quote } from '@lib/helpers/sales-money'
import { resolveSchedule, type ScheduleDue } from '@lib/helpers/sales-schedule'
import type { CalendarDay } from '@lib/validators/temporal.validator'
import { Button } from '@/components/ui/button'
import { Callout } from '@/components/ui/callout'
import { ChoiceCard, ChoiceGroup } from '@/components/ui/choice-card'
import { useToast } from '@/components/ui/toast'
import { FormError } from '@/components/ui/form-error'
import { requestErrorMessage } from '@/helpers/apiClient'
import { salesKeys } from '@/hooks/queryKeys'
import { useChoosePacket, useOpenContract } from '@/hooks/useSales'
import { todayIso } from '@/lib/bookedDays'
import { fmt12, formatEventDate } from '@/lib/eventTime'
import { cn } from '@/lib/utils'
import { SigningFrame, type SigningOutcome } from './SigningFrame'

interface ProposalViewProps {
  eventId: string
  venueName: string
  packet: BookingPacketDto
  /** Its payments are scheduled: the real list replaces the preview. */
  scheduled?: boolean
}

function lineTotal(line: BookingLineDto): number {
  return line.unitCents * line.quantity
}

function LinePrice({ line }: { line: BookingLineDto }) {
  return (
    <span className="shrink-0 text-right tabular-nums text-ink">
      {dollarsFromCents(lineTotal(line))}
      {line.quantity > 1 ? (
        <span className="block text-xs text-ink-muted">
          {line.quantity} × {dollarsFromCents(line.unitCents)}
        </span>
      ) : null}
    </span>
  )
}

/** A choice the couple can make, as a card: the control, the name and words, and the price. */
/** What a line includes, as the venue wrote it, line breaks kept; nothing when it says nothing. */
function lineDescription(line: BookingLineDto): React.ReactNode {
  return line.description === '' ? null : <span className="whitespace-pre-line">{line.description}</span>
}

/** When the security deposit is due, in the couple's words. */
function depositDue(due: ScheduleDue): string {
  if (due.kind === 'on_signing') return 'due when you sign'
  if (due.kind === 'before_event') return `due ${String(due.days)} days before the event`
  return `due ${formatEventDate(due.date)}`
}

/** "due when you sign", "due Mar 1, 2027" */
function dueLabel(dueOn: CalendarDay | null): string {
  return dueOn === null ? 'due when you sign' : `due ${formatEventDate(dueOn)}`
}

/**
 * One proposal as the couple sees it (docs/sales.spec.md, "Choosing and
 * signing"): pick a package and add-ons, and watch the total, tax, service
 * charge and payments follow. Saved choices can change until someone signs.
 */
export function ProposalView({ eventId, venueName, packet, scheduled = false }: ProposalViewProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const choose = useChoosePacket(eventId)
  const openContract = useOpenContract(eventId)
  const [embedUrl, setEmbedUrl] = React.useState<string | null>(null)
  const packages = packet.lines.filter((l) => l.group === 'package')
  const addOns = packet.lines.filter((l) => l.group === 'add_on')
  const included = packet.lines.filter((l) => l.group === 'included')
  const [packageId, setPackageId] = React.useState<string | null>(packages.find((l) => l.chosen)?.id ?? null)
  const [addOnIds, setAddOnIds] = React.useState<string[]>(addOns.filter((l) => l.chosen).map((l) => l.id))
  const [error, setError] = React.useState<string | null>(null)

  const chosen = packet.lines.filter((l) => l.group === 'included' || l.id === packageId || addOnIds.includes(l.id))
  const totals = quote(
    chosen.map((l) => ({ ...l, key: l.id })),
    packet.rates,
  )
  const change = packet.kind === 'amendment'
  const paidBefore = packet.paidBeforeCents ?? 0
  const pickedPackage = packages.length === 0 || packageId !== null
  const resolved = pickedPackage ? resolveSchedule(packet.schedule, Math.max(0, totals.totalCents - paidBefore), packet.date, todayIso()) : null
  const schedule = resolved === null || 'error' in resolved ? resolved : resolved.filter((p) => p.amountCents > 0)
  const saved = packages.every((l) => l.chosen === (l.id === packageId)) && addOns.every((l) => l.chosen === addOnIds.includes(l.id))
  const everChosen = packet.lines.some((l) => l.group !== 'included' && l.chosen)
  // What's saved is what the contract is made from: with a package, one has to be.
  const signable = saved && (packages.length === 0 || packages.some((l) => l.chosen))
  const when =
    packet.startsAt === null || packet.endsAt === null
      ? formatEventDate(packet.date)
      : `${formatEventDate(packet.date)}, ${fmt12(packet.startsAt)} to ${fmt12(packet.endsAt)}`

  function handleSign(): void {
    if (!signable) return setError('Save your choices before signing.')
    setError(null)
    openContract.mutate(packet.id, { onSuccess: (link) => setEmbedUrl(link.embedUrl), onError: (e) => setError(requestErrorMessage(e)) })
  }

  const handleOutcome = React.useCallback(
    (outcome: SigningOutcome) => {
      setEmbedUrl(null)
      void queryClient.invalidateQueries({ queryKey: salesKeys.booking(eventId) })
      if (outcome === 'completed') toast('Everyone has signed. Your copy is on its way to your email.')
      if (outcome === 'signed') toast('Thanks for signing. We’ll email you once everyone has.')
      if (outcome === 'declined') toast(`You declined the ${change ? 'change' : 'contract'}. ${venueName} has been told.`)
    },
    [change, eventId, queryClient, toast, venueName],
  )

  function handleSave(): void {
    if (packages.length > 0 && packageId === null) return setError('Choose a package.')
    setError(null)
    choose.mutate(
      { packetId: packet.id, packageLineId: packageId, addOnLineIds: addOnIds },
      { onSuccess: () => toast('Your choices are saved.'), onError: (e) => setError(requestErrorMessage(e)) },
    )
  }

  return (
    <article className="space-y-8">
      <header className="space-y-1">
        <h2 className="m-0 text-lg font-semibold text-ink">{when}</h2>
        {packet.status === 'signed' ? (
          <Callout tone="success" title="Everyone has signed">
            Your copy of the {change ? 'change' : 'contract'} was emailed to you.
          </Callout>
        ) : null}
        {packet.status !== 'signed' && packet.lapsed ? (
          <Callout tone="neutral" title={`This ${change ? 'change' : 'proposal'} was open until ${formatEventDate(packet.validUntil)}`}>
            {change ? `Your agreement stays as it is. Ask ${venueName} to send it again if you’d still like it.` : `Ask ${venueName} for a new one if you’d still like the date.`}
          </Callout>
        ) : null}
        {packet.status === 'signed' || packet.lapsed ? null : (
          <p className="m-0 text-sm text-ink-muted">
            Open until {formatEventDate(packet.validUntil)}.{' '}
            {change ? 'Until everyone signs it, your agreement stays as it is.' : `${venueName} holds the date for you until then.`}
          </p>
        )}
      </header>

      {packages.length === 0 ? null : (
        <section className="space-y-3">
          <h3 className="m-0 text-sm font-semibold text-ink">{packet.canChoose ? 'Choose your package' : 'Packages'}</h3>
          <ChoiceGroup legend="Packages" hideLegend>
            {packages.map((line) => (
              <ChoiceCard
                key={line.id}
                type="radio"
                name={`package-${packet.id}`}
                checked={packageId === line.id}
                disabled={!packet.canChoose}
                onSelect={() => setPackageId(line.id)}
                title={line.name}
                description={lineDescription(line)}
                aside={<LinePrice line={line} />}
              />
            ))}
          </ChoiceGroup>
        </section>
      )}

      {addOns.length === 0 ? null : (
        <section className="space-y-3">
          <h3 className="m-0 text-sm font-semibold text-ink">{packet.canChoose ? 'Add anything you’d like' : 'Add-ons'}</h3>
          <ChoiceGroup legend="Add-ons" hideLegend>
            {addOns.map((line) => (
              <ChoiceCard
                key={line.id}
                type="checkbox"
                checked={addOnIds.includes(line.id)}
                disabled={!packet.canChoose}
                onCheckedChange={(on) => setAddOnIds((all) => (on ? [...all, line.id] : all.filter((id) => id !== line.id)))}
                title={line.name}
                description={lineDescription(line)}
                aside={<LinePrice line={line} />}
              />
            ))}
          </ChoiceGroup>
        </section>
      )}

      {included.length === 0 ? null : (
        <section className="space-y-2">
          <h3 className="m-0 text-sm font-semibold text-ink">Included</h3>
          <ul className="m-0 list-none divide-y divide-line rounded-lg border border-line p-0">
            {included.map((line) => (
              <li key={line.id} className="flex items-start justify-between gap-3 px-4 py-3 text-sm">
                <span className="min-w-0">
                  <span className="block text-ink">{line.name}</span>
                  {line.description === '' ? null : <span className="block text-ink-muted">{line.description}</span>}
                </span>
                <LinePrice line={line} />
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="space-y-2 rounded-lg border border-line bg-surface-sunken p-4">
        {pickedPackage ? null : <p className="m-0 text-sm text-ink-muted">Choose a package to see your total.</p>}
        <dl className={cn('m-0 space-y-1 text-sm', !pickedPackage && 'hidden')}>
          <div className="flex justify-between gap-3">
            <dt className="text-ink-muted">Subtotal</dt>
            <dd className="m-0 tabular-nums text-ink">{dollarsFromCents(totals.subtotalCents)}</dd>
          </div>
          {packet.rates.serviceChargeMilli === 0 ? null : (
            <div className="flex justify-between gap-3">
              <dt className="text-ink-muted">Service charge ({milliToPercent(packet.rates.serviceChargeMilli)}%)</dt>
              <dd className="m-0 tabular-nums text-ink">{dollarsFromCents(totals.serviceChargeCents)}</dd>
            </div>
          )}
          {packet.rates.taxMilli === 0 ? null : (
            <div className="flex justify-between gap-3">
              <dt className="text-ink-muted">Tax ({milliToPercent(packet.rates.taxMilli)}%)</dt>
              <dd className="m-0 tabular-nums text-ink">{dollarsFromCents(totals.taxCents)}</dd>
            </div>
          )}
          <div className="flex justify-between gap-3 border-t border-line pt-2 text-base font-semibold">
            <dt className="text-ink">Total</dt>
            <dd className="m-0 tabular-nums text-ink">{dollarsFromCents(totals.totalCents)}</dd>
          </div>
          {paidBefore > 0 ? (
            <>
              <div className="flex justify-between gap-3">
                <dt className="text-ink-muted">Paid so far</dt>
                <dd className="m-0 tabular-nums text-ink">−{dollarsFromCents(paidBefore)}</dd>
              </div>
              <div className="flex justify-between gap-3 font-semibold">
                <dt className="text-ink">Left to pay</dt>
                <dd className="m-0 tabular-nums text-ink">{dollarsFromCents(Math.max(0, totals.totalCents - paidBefore))}</dd>
              </div>
            </>
          ) : null}
        </dl>
      </section>

      <section className={cn('space-y-2', scheduled && 'hidden')}>
        <h3 className="m-0 text-sm font-semibold text-ink">Payments</h3>
        {schedule === null ? <p className="m-0 text-sm text-ink-muted">Choose a package to see your payments.</p> : null}
        {schedule !== null && 'error' in schedule ? <p className="m-0 text-sm text-ink-muted">{venueName} will confirm your payments.</p> : null}
        {schedule !== null && !('error' in schedule) ? (
          <ul className="m-0 list-none space-y-1 p-0 text-sm">
            {schedule.map((p, i) => (
              <li key={`${p.label}-${String(i)}`} className="flex justify-between gap-3">
                <span className="text-ink">
                  {p.label} <span className="text-ink-muted">{dueLabel(p.dueOn)}</span>
                </span>
                <span className="tabular-nums text-ink">{dollarsFromCents(p.amountCents)}</span>
              </li>
            ))}
          </ul>
        ) : null}
        {packet.securityDeposit === null ? null : (
          <p className="m-0 text-sm text-ink-muted">
            Plus a refundable security deposit of {dollarsFromCents(packet.securityDeposit.cents)}, {depositDue(packet.securityDeposit.due)}.
          </p>
        )}
        {packet.bankDiscountMilli === 0 ? null : (
          <p className="m-0 text-sm text-ink-muted">Paying by bank transfer takes {milliToPercent(packet.bankDiscountMilli)}% off each payment.</p>
        )}
      </section>

      <footer className="space-y-3">
        <ul className="m-0 flex list-none flex-wrap gap-x-4 gap-y-1 p-0 text-sm">
          {packet.signers.map((s) => (
            <li key={s.name} className={cn('inline-flex items-center gap-1', s.signed ? 'text-ok-ink' : 'text-ink-muted')}>
              {s.signed ? <Check className="h-3.5 w-3.5" /> : null}
              {s.name} {s.signed ? 'signed' : 'hasn’t signed yet'}
            </li>
          ))}
        </ul>
        {error === null ? null : <FormError>{error}</FormError>}
        <div className="flex flex-wrap items-center gap-3">
          {packet.canChoose && packages.length + addOns.length > 0 ? (
            <Button variant={packet.canSign ? 'outline' : 'default'} onClick={handleSave} disabled={choose.isPending || (saved && (everChosen || signable))}>
              {choose.isPending ? 'Saving…' : 'Save my choices'}
            </Button>
          ) : null}
          {packet.canSign ? (
            <Button onClick={handleSign} disabled={openContract.isPending}>
              {openContract.isPending ? 'Opening…' : 'Review and sign'}
            </Button>
          ) : null}
          {packet.canChoose && saved && everChosen ? <span className="text-sm text-ink-muted">Saved. You can change them until someone signs.</span> : null}
        </div>
      </footer>
      <SigningFrame
        embedUrl={embedUrl}
        title={change ? 'The change to your agreement' : 'Your contract'}
        description={`Read it through, then sign. ${venueName} countersigns once everyone has.`}
        onClose={() => {
          setEmbedUrl(null)
          void queryClient.invalidateQueries({ queryKey: salesKeys.booking(eventId) })
        }}
        onOutcome={handleOutcome}
      />
    </article>
  )
}
